import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseUrls } from '@app/app.constants';
import { Device } from '@app/core/interfaces/c5/device';
import { FunctionKey } from '@app/core/interfaces/c5/function-key';
import { PhoneNumber } from '@app/core/interfaces/c5/phone-number';
import { DeviceUpdate } from '@app/core/interfaces/frontend/device-update';
import { SimpleDevice } from '@app/core/interfaces/portal/simple-device';
import { SimplePhone } from '@app/core/interfaces/portal/simple-phone';
import { UserLevel } from '@app/core/models/user-level';
import { Utils } from '@app/shared/services/utils.service';
import { wrapError } from '@app/utils/rxjs';
import { AddPhoneNumberMappingCommand } from '@business/devices/models/add-phone-number-mapping-command.enum';
import { DeviceAction } from '@business/devices/models/device-action.enum';
import { Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { PhoneRingers } from '../interfaces/c5/phone';

@Injectable({
  providedIn: 'root',
})
export class DeviceRepository {
  constructor(
    private http: HttpClient,
    private utils: Utils
  ) {}

  createGenericDevice(): Observable<Device> {
    return this.utils.apiUrl('/genericDevice').pipe(
      mergeMap((url) => this.http.post<Device>(url, {})),
      wrapError('device-repository:create-generic-device')
    );
  }

  createNumberMapping(device: Device, phoneNumber: PhoneNumber, command: AddPhoneNumberMappingCommand) {
    const url = {
      ALL: () => `/devices/${device.id}/numberMapping/all`,
      ALL_INTERNAL: () => `/devices/${device.id}/numberMapping/allInternal`,
      ALL_FIXED_NETWORK: () => `/devices/${device.id}/numberMapping/allFixedNetwork`,
      ALL_MOBILE: () => `/devices/${device.id}/numberMapping/allMobile`,
      SINGLE: () => `/devices/${device.id}/numberMapping/phoneNumber/${phoneNumber.id}`,
    }[AddPhoneNumberMappingCommand[command]]();

    return this.utils.apiUrl(url).pipe(
      mergeMap((url) => this.http.post<void>(url, {})),
      wrapError('device-service:create-number-mapping')
    );
  }

  createPhoneDevice(model: SimplePhone): Observable<void> {
    return this.utils
      .apiUrl({
        [UserLevel.ServiceProviderAdmin]: `${BaseUrls.ServiceProvider.EndUser}/phones`,
        [UserLevel.EnterpriseAdmin]: `${BaseUrls.Enterprise.EndUser}/phones`,
        // Not yet implemented in backend
        // [UserLevel.EndUser]: `${BaseUrls.EndUser}/phones/${id}`,
      })
      .pipe(
        mergeMap((url) => this.http.post<void>(url, model)),
        wrapError('device-repository:create-phone-device')
      );
  }

  /**
   * Delete a device by its id.
   * @param id Id of the device.
   * @param userId Optional user id (if the device should be deleted from a higher user level)
   */
  delete(id: number, userId?: string): Observable<void> {
    return this.utils
      .apiUrl(
        {
          [UserLevel.ServiceProviderAdmin]: `${BaseUrls.ServiceProvider.EndUser}/devices/${id}`,
          [UserLevel.EnterpriseAdmin]: `${BaseUrls.Enterprise.EndUser}/devices/${id}`,
          [UserLevel.EndUser]: `${BaseUrls.EndUser}/devices/${id}`,
        },
        {
          userId,
        }
      )
      .pipe(
        mergeMap((url) => this.http.delete<void>(url)),
        wrapError('device-repository:delete')
      );
  }

  deleteClientLoggingConfig(clientLoggingConfigId: number): Observable<void> {
    return this.utils.apiUrl(`/clientLoggingConfig/${clientLoggingConfigId}`).pipe(
      mergeMap((url) => this.http.delete<void>(url)),
      wrapError('device-repository:delete-client-logging-config')
    );
  }

  deleteGenericDevice(id: number): Observable<void> {
    return this.utils.apiUrl(`/genericDevice/${id}`).pipe(
      mergeMap((url) => this.http.delete<void>(url)),
      wrapError('device-repository:delete-generic-device')
    );
  }

  deleteNumberMapping(deviceId: number, phoneNumberId: number) {
    return this.utils.apiUrl(`/devices/${deviceId}/phoneNumbers/${phoneNumberId}`).pipe(
      mergeMap((url) => this.http.delete<void>(url)),
      wrapError('device-repository:delete-number-mapping')
    );
  }

  distributeFunctionKeys(
    functionKeys: FunctionKey[],
    overwrite: boolean,
    userId: string,
    phoneId: string
  ): Observable<void> {
    return this.utils
      .apiUrl(`/updatePhoneFunctionKeys/${phoneId}`, {
        userId,
      })
      .pipe(
        mergeMap((url) =>
          this.http.put<void>(url, functionKeys, {
            params: {
              overwrite: overwrite ? 'true' : 'false',
            },
          })
        ),
        wrapError('device-repository:distribute-function-keys')
      );
  }

  executeDeviceAction(mac: string, action: DeviceAction) {
    return this.utils.routeBasedApiUrl(`/devices/${mac}/action/${action}`, UserLevel.EnterpriseAdmin).pipe(
      mergeMap((url) =>
        this.http.put<void>(url, {
          action: DeviceAction[action],
          mac,
        })
      ),
      wrapError('device-repository:execute-device-action')
    );
  }

  list(): Observable<Device[]> {
    return this.utils.apiUrl('/devices').pipe(
      mergeMap((url) => this.http.get<Device[]>(url)),
      wrapError('device-repository:list')
    );
  }

  listCstaDevices(): Observable<Device[]> {
    return this.utils.apiUrl('/cstaDevices').pipe(
      mergeMap((url) => this.http.get<Device[]>(url)),
      wrapError('device-repository:list-csta-devices')
    );
  }

  listFunctionKeys(deviceId: number): Observable<FunctionKey[]> {
    const url = `/devices/${deviceId}/funcKeys`;

    const urls = {
      [UserLevel.ServiceProviderAdmin]: BaseUrls.ServiceProvider.Enterprise + url,
      [UserLevel.EnterpriseAdmin]: BaseUrls.Enterprise.Enterprise + url,
      [UserLevel.EndUser]: BaseUrls.EndUser + url,
    };

    return this.utils.apiUrl(urls).pipe(
      mergeMap((url) => this.http.get<FunctionKey[]>(url)),
      wrapError('device-repository:list-function-keys')
    );
  }

  resetGenericDevicePassword(newPassword?: string): Observable<Device> {
    return this.utils.apiUrl('/genericDevice/resetGenericDevicePassword').pipe(
      mergeMap((url) =>
        this.http.put<Device>(url, {
          newPassword,
        })
      ),
      wrapError('device-repository:reset-generic-device-password')
    );
  }

  getGenericDevicePassword(deviceId: number): Observable<string> {
    return this.utils.apiUrl(`/genericDevice/${deviceId}/password`).pipe(
      mergeMap((url) => this.http.get<string>(url)),
      wrapError('device-repository:get-generic-device-password')
    );
  }

  update(update: DeviceUpdate, userId?: string): Observable<void> {
    const { id, ...value } = update;

    return this.utils
      .apiUrl(
        {
          [UserLevel.ServiceProviderAdmin]: `${BaseUrls.ServiceProvider.EndUser}/phones/${id}`,
          [UserLevel.EnterpriseAdmin]: `${BaseUrls.Enterprise.EndUser}/phones/${id}`,
          // Not yet implemented in backend
          // [UserLevel.EndUser]: `${BaseUrls.EndUser}/phones/${id}`,
        },
        {
          userId,
        }
      )
      .pipe(
        mergeMap((url) => this.http.put<void>(url, value)),
        wrapError('device-repository:update')
      );
  }

  updateDevice(id: number, model: SimpleDevice): Observable<void> {
    return this.utils.apiUrl(`/devices/${id}`).pipe(
      mergeMap((url) => this.http.put<void>(url, model)),
      wrapError('device-repository:update-device')
    );
  }

  updateFunctionKeys(deviceId: number, functionKeys: FunctionKey[]): Observable<void> {
    const url = `/devices/${deviceId}/funcKeys`;

    const urls = {
      [UserLevel.ServiceProviderAdmin]: BaseUrls.ServiceProvider.Enterprise + url,
      [UserLevel.EnterpriseAdmin]: BaseUrls.Enterprise.Enterprise + url,
      [UserLevel.EndUser]: BaseUrls.EndUser + url,
    };

    return this.utils.apiUrl(urls).pipe(
      mergeMap((url) => this.http.put<void>(url, functionKeys)),
      wrapError('device-repository:update-function-keys')
    );
  }

  updateOutgoingIdentity(
    device: Device,
    phoneNumberId: number,
    identity: 'EXTERNAL' | 'INTERNAL' | 'SMS'
  ): Observable<Device> {
    const url = {
      EXTERNAL: `/devices/${device.id}/externalIdentity/${phoneNumberId}`,
      INTERNAL: `/devices/${device.id}/internalIdentity/${phoneNumberId}`,
      SMS: `/devices/${device.id}/smsIdentity/${phoneNumberId}`,
    }[identity];

    return this.utils.apiUrl(url).pipe(
      mergeMap((url) => this.http.put<Device>(url, {})),
      wrapError('device-repository:update-outgoing-identity')
    );
  }

  updateDeviceRingers(device: Device, ringers: PhoneRingers): Observable<void> {
    const url = `/devices/${device.id}/ringtone`;

    return this.utils.apiUrl(url).pipe(
      mergeMap((url) => this.http.put<void>(url, ringers)),
      wrapError('device-repository:update-device-ringers')
    );
  }

  updatePhoneDevice(id: number, model: SimplePhone): Observable<void> {
    return this.utils
      .apiUrl({
        [UserLevel.ServiceProviderAdmin]: `${BaseUrls.ServiceProvider.EndUser}/phones/${id}`,
        [UserLevel.EnterpriseAdmin]: `${BaseUrls.Enterprise.EndUser}/phones/${id}`,
        // Not yet implemented in backend
        // [UserLevel.EndUser]: `${BaseUrls.EndUser}/phones/${id}`,
      })
      .pipe(
        mergeMap((url) => this.http.put<void>(url, model)),
        wrapError('device-repository:update-phone-device')
      );
  }

  deletePushToken(pushTokenId: number): Observable<void> {
    return this.utils.apiUrl(`/pushToken/${pushTokenId}`).pipe(
      mergeMap((url) => this.http.delete<void>(url)),
      wrapError('device-repository:delete-push-token')
    );
  }
}
